import React, { useState, useEffect } from 'react';
import { statusCodesService, StatusCodeDto } from '../../../../services/statusCodesService';

interface StatusBadgeProps {
  value: number;
  statusType: number;
  /** Custom labels bypass API lookup — used for legacy mappings not in StatusCodes table */
  customLabels?: Record<number, string>;
}

// Color mapping for StcTrans LineType (legacy warehouse operation types)
const stcTransLineTypeColors: Record<number, string> = {
  0: 'bg-green-100 text-green-700',    // Varemottak
  1: 'bg-orange-100 text-orange-700',   // Korreksjon
  2: 'bg-blue-100 text-blue-700',       // Overføring
  3: 'bg-amber-100 text-amber-700',     // Korreksjon (GR)
  4: 'bg-purple-100 text-purple-700',   // Lagertelling
};

// Color mapping based on status code values
const getStatusColor = (statusCode: number, statusType: number, hasCustomLabels?: boolean): string => {
  // Custom labels with their own color scheme
  if (hasCustomLabels) {
    return stcTransLineTypeColors[statusCode] || 'bg-gray-100 text-gray-700';
  }

  // General status type pattern (ST1 - Order/PList statuses)
  if (statusType === 1) {
    if (statusCode === 0) return 'bg-gray-100 text-gray-700';
    if (statusCode >= 1 && statusCode <= 10) return 'bg-blue-100 text-blue-700';
    if (statusCode >= 11 && statusCode <= 20) return 'bg-yellow-100 text-yellow-700';
    if (statusCode >= 21 && statusCode <= 30) return 'bg-orange-100 text-orange-700';
    if (statusCode >= 31 && statusCode <= 40) return 'bg-green-100 text-green-700';
    if (statusCode >= 41 && statusCode <= 50) return 'bg-teal-100 text-teal-700';
    if (statusCode >= 51) return 'bg-purple-100 text-purple-700';
  }

  // ST2 - Lock status
  if (statusType === 2) {
    if (statusCode === 0) return 'bg-green-100 text-green-700';
    return 'bg-red-100 text-red-700';
  }

  // ST25 - Internal transfer TransType
  if (statusType === 25) {
    if (statusCode <= 10) return 'bg-blue-100 text-blue-700';
    if (statusCode <= 20) return 'bg-teal-100 text-teal-700';
    return 'bg-purple-100 text-purple-700';
  }

  // ST27 - StcTransaction TransType
  if (statusType === 27) {
    if (statusCode === 10) return 'bg-yellow-100 text-yellow-700';  // Plukk
    if (statusCode === 20) return 'bg-green-100 text-green-700';    // Mottak
    if (statusCode === 15) return 'bg-orange-100 text-orange-700';  // Korreksjon
    return 'bg-blue-100 text-blue-700';
  }

  // ST32 - Item type
  if (statusType === 32) {
    if (statusCode === 1) return 'bg-gray-100 text-gray-700';
    if (statusCode === 2) return 'bg-blue-100 text-blue-700';
    if (statusCode === 4) return 'bg-orange-100 text-orange-700';
    if (statusCode === 5) return 'bg-teal-100 text-teal-700';
    if (statusCode === 6) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  }

  // ST39 - Reservation status (0=Aktiv, 1=Frigjort)
  if (statusType === 39) {
    if (statusCode === 0) return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-500';
  }

  // Default
  if (statusCode === 0) return 'bg-gray-100 text-gray-700';
  return 'bg-blue-100 text-blue-700';
};

// Cache for status code lookups (shared across all instances)
const statusCache = new Map<string, StatusCodeDto[]>();

export const StatusBadge: React.FC<StatusBadgeProps> = ({ value, statusType, customLabels }) => {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    // If custom labels provided, use them directly (no API call)
    if (customLabels) {
      setLabel(customLabels[value] || null);
      return;
    }

    let cancelled = false;

    const lookup = async () => {
      const cacheKey = `${statusType}`;
      let codes = statusCache.get(cacheKey);

      if (!codes) {
        try {
          codes = await statusCodesService.getByStatusType(statusType);
          statusCache.set(cacheKey, codes);
        } catch {
          // Silently fail — show numeric value
          return;
        }
      }

      if (cancelled) return;

      const match = codes.find(c => c.statusCode === value);
      if (match) {
        setLabel(match.lng_1);
      }
    };

    lookup();
    return () => { cancelled = true; };
  }, [value, statusType, customLabels]);

  const colorClasses = getStatusColor(value, statusType, !!customLabels);
  const displayText = label ? `${value} - ${label}` : String(value);

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
      {displayText}
    </span>
  );
};
